import React, { useState, useEffect, useContext } from "react";
import { Form } from "react-bootstrap";
import * as XLSX from 'xlsx-js-style';
import moment from "moment";
import { ModalContext } from "../../utils/Context";

const ExportFundModal = ({ fundsList, onHide, fetchFirstInvoiceDetails }) => {
    const [exportIncludeInvoices, setExportIncludeInvoices] = useState(true);
    const [selectedFundsForExport, setSelectedFundsForExport] = useState([]);
    const [selectAllFunds, setSelectAllFunds] = useState(true);
    const [exporting, setExporting] = useState(false);
    const modalContext = useContext(ModalContext);
    const { handleModalData } = modalContext;
    const { closeModal } = modalContext;

    useEffect(() => {
        if (fundsList.length > 0) {
            setSelectedFundsForExport(fundsList.map(fund => fund.FundNo));
        }
    }, [fundsList]);

    const toggleFundSelection = (fundNo) => {
        setSelectedFundsForExport(prev => {
            if (prev.includes(fundNo)) {
                return prev.filter(f => f !== fundNo);
            } else {
                return [...prev, fundNo];
            }
        });
    };

    const toggleSelectAllFunds = () => {
        if (selectAllFunds) {
            setSelectedFundsForExport([]);
        } else {
            setSelectedFundsForExport(fundsList.map(fund => fund.FundNo));
        }
        setSelectAllFunds(!selectAllFunds);
    };

    const getUniqueInvoicesForSelectedFunds = (selectedFunds) => {
        const invoiceMap = new Map();
        selectedFunds.forEach(fund => {
            if (fund.Invoices && fund.Invoices.length > 0) {
                fund.Invoices.forEach(invoice => {
                    const key = invoice.DocumentNo || invoice.InvoiceId;
                    if (!invoiceMap.has(key)) {
                        invoiceMap.set(key, invoice);
                    }
                });
            }
        });
        return Array.from(invoiceMap.values());
    };

    const getInvoiceData = (invoiceDetails) => {
        const formattedBillDate = moment(invoiceDetails?.BillDate).format("D/MM/YYYY");

        const headerData = [
            ['Bill No:', invoiceDetails.InvDocumentNo || 'N/A', '', '', '', 'Bill Date:', formattedBillDate || 'N/A'],
            ['Vendor:', invoiceDetails.VendorName || 'Prospect Vendor', '', '', '', 'Status:', invoiceDetails.StatusName || 'N/A'],
            [],
        ];

        const tableHeader = [
            'Bill Date',
            'Vendor Name',
            'Item',
            'Description',
            'Department',
            'Amount',
            'Comment'
        ];

        const itemData = invoiceDetails.InvoiceLines.map(item => [
            moment(item.BillDate).format("D/MM/YYYY"),
            item.VendorName,
            item.ItemName,
            item.Description || 'N/A',
            item.DepartmentName,
            `${item?.Amount?.toFixed(2)}`,
            item?.Comment || 'N/A'
        ]);

        const totalAmount = invoiceDetails?.Total?.toFixed(2) || '0.00';

        return [
            ...headerData,
            tableHeader,
            ...itemData,
            [],
            ['', '', '', '', 'Total:', `${totalAmount}`, '']
        ];
    };

    const getFundsReportData = (selectedFunds) => {
        const exportData = [];
        const invoiceSheetNames = {};

        if (exportIncludeInvoices) {
            const uniqueInvoices = getUniqueInvoicesForSelectedFunds(selectedFunds);
            uniqueInvoices.forEach(invoice => {
                const docNo = invoice.DocumentNo || `Invoice_${invoice.InvoiceId}`;
                const cleanName = docNo.replace(/[\\/*?[\]:]/g, '').substring(0, 31);
                invoiceSheetNames[invoice.InvoiceId] = cleanName;
            });
        }

        exportData.push([
            "Sr No.", "Fund No.", "Created Date", "Description",
            "Fund Amount (₹)", "Expense Amount (₹)", "Remaining Balance (₹)",
            "Status", "Allocated Invoices", "Invoice Date"
        ]);

        selectedFunds.forEach((fund, index) => {
            const isActive = fund.RemainingAmount > 0;
            const status = isActive ? "ACTIVE" : "CLOSED";

            exportData.push([
                index + 1,
                fund.FundNo,
                moment(fund.CreatedAt).format("DD-MM-YYYY"),
                fund.Description || "N/A",
                parseFloat(fund.FundAmount).toFixed(2),
                "",
                parseFloat(fund.RemainingAmount).toFixed(2),
                status,
                "",
                ""
            ]);

            if (fund.Invoices && fund.Invoices.length > 0) {
                let runningBalance = fund.FundAmount;

                fund.Invoices.forEach(invoice => {
                    const fundAllocation = invoice.Funds?.find(f => f.FundNo === fund.FundNo);
                    if (fundAllocation) {
                        const expenseAmount = fundAllocation.AllocatedAmount;
                        runningBalance -= expenseAmount;

                        let invoiceCell = invoice.DocumentNo || "N/A";
                        if (exportIncludeInvoices && invoiceSheetNames[invoice.InvoiceId]) {
                            invoiceCell = {
                                t: 's',
                                v: invoice.DocumentNo || "N/A",
                                l: { Target: `#'${invoiceSheetNames[invoice.InvoiceId]}'!A1` },
                                s: {
                                    font: {
                                        color: { rgb: "0000FF" },
                                        underline: true
                                    }
                                }
                            };
                        }

                        exportData.push([
                            "", "", "", "", "",
                            expenseAmount.toFixed(2),
                            runningBalance.toFixed(2),
                            "",
                            invoiceCell,
                            moment(invoice.BillDate).format("DD-MM-YYYY")
                        ]);
                    }
                });
            }
        });

        const selectedTotalAmount = selectedFunds.reduce(
            (sum, fund) => sum + parseFloat(fund.FundAmount), 0
        );

        const selectedBalanceAmount = selectedFunds.reduce(
            (sum, fund) => sum + parseFloat(fund.RemainingAmount), 0
        );

        const selectedExpenses = selectedTotalAmount - selectedBalanceAmount;

        const activeFundsCount = selectedFunds.filter(
            f => f.RemainingAmount > 0
        ).length;

        const closedFundsCount = selectedFunds.filter(
            f => f.RemainingAmount <= 0
        ).length;

        exportData.push([]);
        exportData.push(["SUMMARY", "", "", "", "", "", "", "", ""]);
        exportData.push([
            "Total Funds Added", "", "", "",
            `₹ ${selectedTotalAmount.toFixed(2)}`,
            "", "", "", ""
        ]);
        exportData.push([
            "Total Expenses", "", "", "", "",
            `₹ ${selectedExpenses.toFixed(2)}`,
            "", "", ""
        ]);
        exportData.push([
            "Total Remaining Balance(Status : Active)", "", "", "", "", "",
            `₹ ${selectedBalanceAmount.toFixed(2)}`,
            "", ""
        ]);
        exportData.push([
            "Active Funds", activeFundsCount,
            "", "", "", "", "", "", ""
        ]);
        exportData.push([
            "Closed Funds", closedFundsCount,
            "", "", "", "", "", "", ""
        ]);

        return exportData;
    };

    const handleExport = async () => {
        setExporting(true);

        try {
            const wb = XLSX.utils.book_new();

            const selectedFunds = fundsList.filter(fund =>
                selectedFundsForExport.includes(fund.FundNo)
            );

            const fundsData = getFundsReportData(selectedFunds);
            const fundsWS = XLSX.utils.aoa_to_sheet(fundsData);
            XLSX.utils.book_append_sheet(wb, fundsWS, "Petty Cash Funds");

            if (exportIncludeInvoices && selectedFundsForExport.length > 0) {
                const uniqueInvoices = getUniqueInvoicesForSelectedFunds(selectedFunds);

                for (const invoice of uniqueInvoices) {
                    let invoiceDetails;
                    try {
                        invoiceDetails = await fetchFirstInvoiceDetails(invoice.InvoiceId);
                    } catch (error) {
                        console.error(`Error fetching invoice ${invoice.InvoiceId}:`, error);
                        continue;
                    }

                    if (!invoiceDetails) continue;

                    const invoiceData = getInvoiceData(invoiceDetails);
                    let sheetName = invoiceDetails.InvDocumentNo || `Invoice_${invoice.InvoiceId}`;

                    sheetName = sheetName
                        .replace(/[\\/*?[\]:]/g, '')
                        .substring(0, 31);

                    let uniqueSheetName = sheetName;
                    let counter = 1;
                    while (wb.SheetNames.includes(uniqueSheetName)) {
                        uniqueSheetName = `${sheetName.substring(0, 28)}_${counter}`;
                        counter++;
                    }

                    const invoiceWS = XLSX.utils.aoa_to_sheet(invoiceData);
                    XLSX.utils.book_append_sheet(wb, invoiceWS, uniqueSheetName);
                }
            }

            XLSX.writeFile(wb, "Petty_Cash_Report.xlsx");
        } catch (error) {
            console.error("Export error:", error);
        } finally {
            setExporting(false);
            closeModal();
        }
    };

    return (
        <div className="cstm-frm-mdl">
            <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="modal-title">Export</h4>
            </div>
            <div className="modal-body modal-body-fund">
                <div className="mb-3">
                    <h5>Select Funds to Export</h5>
                    <Form.Check
                        type="checkbox"
                        label="Select All Funds"
                        checked={selectAllFunds}
                        onChange={toggleSelectAllFunds}
                        className="mb-3"
                    />

                    <div className="fund-selection-container">
                        {fundsList.map(fund => (
                            <Form.Check
                                key={fund.FundNo}
                                type="checkbox"
                                id={`fund-${fund.FundNo}`}
                                label={`${fund.FundNo} (₹${parseFloat(fund.FundAmount).toFixed(2)})`}
                                checked={selectedFundsForExport.includes(fund.FundNo)}
                                onChange={() => toggleFundSelection(fund.FundNo)}
                                className="mb-2"
                            />
                        ))}
                    </div>
                </div>

                <div className="">
                    <Form.Check
                        type="checkbox"
                        label="Include Invoice Details"
                        checked={exportIncludeInvoices}
                        onChange={(e) => setExportIncludeInvoices(e.target.checked)}
                    />
                    <small className="text-muted">
                        Includes a separate sheet for each invoice related to selected funds
                    </small>
                </div>
            </div>
            <div className="modal-footer">
                <div className="d-flex align-items-center mb-20">
                    <div className="danger-btn">
                        <button
                            type="button"
                            className="btn"
                            onClick={() => closeModal()}
                            disabled={exporting}
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="success-btn ms-2">
                        <button
                            type="button"
                            className="btn"
                            onClick={handleExport}
                        // disabled={exporting || selectedFundsForExport.length === 0}
                        >
                            {exporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                "Export"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportFundModal;